import { useEffect, useRef, useState } from 'react'
import { BsChevronDown } from 'react-icons/bs'
import { FaGithub, FaLinkedinIn } from 'react-icons/fa'
import { MdOutlineEmail } from 'react-icons/md'
import { TypeAnimation } from 'react-type-animation'
import SkillsGrid from '../skillsgrid/SkillsGrid'
import './Home.css'

const SKILLS_VIEWED_SESSION_KEY = 'home-skills-viewed'

function Home() {
  const skillsPanelRef = useRef(null)
  const [showScrollIndicator, setShowScrollIndicator] = useState(false)
  const [skillsViewedThisSession, setSkillsViewedThisSession] = useState(() => {
    return sessionStorage.getItem(SKILLS_VIEWED_SESSION_KEY) === 'true'
  })

  useEffect(() => {
    const updateScrollIndicator = () => {
      const skillsPanel = skillsPanelRef.current

      if (!skillsPanel || skillsViewedThisSession) {
        setShowScrollIndicator(false)
        return
      }

      const nextVisible =
        skillsPanel.getBoundingClientRect().top > window.innerHeight - 48

      if (nextVisible) {
        setShowScrollIndicator(true)
        return
      }

      setShowScrollIndicator(false)
      setSkillsViewedThisSession(true)
      sessionStorage.setItem(SKILLS_VIEWED_SESSION_KEY, 'true')
    }

    updateScrollIndicator()
    window.addEventListener('scroll', updateScrollIndicator, { passive: true })
    window.addEventListener('resize', updateScrollIndicator)

    return () => {
      window.removeEventListener('scroll', updateScrollIndicator)
      window.removeEventListener('resize', updateScrollIndicator)
    }
  }, [skillsViewedThisSession])

  const scrollToSkills = () => {
    setShowScrollIndicator(false)
    setSkillsViewedThisSession(true)
    sessionStorage.setItem(SKILLS_VIEWED_SESSION_KEY, 'true')
    skillsPanelRef.current?.scrollIntoView({
      behavior: 'smooth',
      block: 'start',
    })
  }

  return (
    <section className="home-page">
      <div className="home-content" id="home">
        <div className="home-hero">
          <div className="home-copy">
            <TypeAnimation
              sequence={[
                ' ',
                1000,
                ' Hi there 👋',
                1500,
                " Hi there 👋 I'm Alan",
                1000,
              ]}
              className="hero-title"
              cursor={false}
              preRenderFirstString
            />
          </div>

          <aside className="portrait-panel">
            <img
              src="Portrait1.png"
              alt="Alan Pham portrait"
              className="portrait-image"
            />
          </aside>

          <div className="home-pill-row" aria-label="Core disciplines">
            <span className="home-pill">Software Engineering</span>
            <span className="home-pill">Data Science</span>
            <span className="home-pill">User Interface Design</span>
          </div>

          <div className="intro-text">
            <p>
              I’m a software engineer and data scientist with an analytically
              minded approach and a passion for solving complex problems through
              creativity and persistence. Curiosity and a methodical mindset
              shape how I work: questioning assumptions, reasoning through
              system behavior, exploring edge cases, and testing exhaustively
              until outcomes feel both reliable and elegant. That same care
              carries me into building intuitive user experiences and making
              sound, data-informed decisions. In collaborative environments, I
              bring a proactive, solutions-focused attitude along with steady
              grit and focus that turn small improvements into meaningful impact
              for my teams.
            </p>
          </div>

          <div className="home-links-row">
            <div className="social-links">
              <a
                href="https://www.linkedin.com/in/alanpham5/"
                className="social-link"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="LinkedIn"
              >
                <FaLinkedinIn className="social-icon" aria-hidden="true" />
              </a>
              <a
                href="https://github.com/alanpham5"
                className="social-link"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="GitHub"
              >
                <FaGithub className="social-icon" aria-hidden="true" />
              </a>
              <a
                href="mailto:alanpham0619@gmail.com"
                className="social-link"
                aria-label="Email"
              >
                <MdOutlineEmail className="social-icon" aria-hidden="true" />
              </a>
            </div>
          </div>
        </div>

        <section
          ref={skillsPanelRef}
          className="skills-panel"
          aria-labelledby="skills-title"
        >
          <h5 className="skills-title" id="skills-title">
            Languages and Tools I’ve Worked With
          </h5>
          <SkillsGrid />
        </section>
      </div>

      <button
        type="button"
        className={`home-scroll-indicator ${showScrollIndicator ? 'is-visible' : ''}`}
        onClick={scrollToSkills}
        aria-label="Scroll down to the skills grid"
        title="Scroll down to the skills grid"
      >
        <BsChevronDown
          className="home-scroll-indicator-icon"
          aria-hidden="true"
        />
      </button>
    </section>
  )
}

export default Home
